import React, { useState } from 'react';
import Card from "../component/Card";
import { products, rawMaterials } from "../data/data";
import { motion } from "framer-motion";
import { FaSearch, FaTimes } from "react-icons/fa";

const Services = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // all, products, materials

  // Variants for animation
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    show: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  // Filter items based on search and active tab
  const filterItems = (items) => {
    return items.filter(item =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredProducts = filterItems(products);
  const filteredMaterials = filterItems(rawMaterials);

  const tabs = [
    { id: "all", label: "All Items", count: products.length + rawMaterials.length },
    { id: "products", label: "Products", count: products.length },
    { id: "materials", label: "Raw Materials", count: rawMaterials.length },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-secondary/30 to-white">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative bg-gradient-primary text-white py-20 md:py-28 mt-20 md:mt-24 overflow-hidden"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="relative container mx-auto px-6 md:px-16 lg:px-40 text-center">
          <motion.h1
            variants={headerVariants}
            initial="hidden"
            animate="show"
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
          >
            Our Services
          </motion.h1>
          <motion.p
            variants={headerVariants}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto"
          >
            Discover our comprehensive range of high-quality animal feeds and raw materials
          </motion.p>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="container mx-auto px-6 md:px-16 lg:px-40 py-12">
        {/* Search and Filter Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto mb-8">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              placeholder="Search products or materials..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-12 py-4 border-2 border-gray-200 rounded-2xl shadow-sm focus:outline-none focus:border-primary focus:shadow-primary/20 transition-all duration-300 text-lg"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
              >
                <FaTimes className="text-lg" />
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-3">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  activeTab === tab.id
                    ? "bg-gradient-primary text-white shadow-primary"
                    : "bg-white text-gray-700 border-2 border-gray-200 hover:border-primary hover:text-primary"
                }`}
              >
                {tab.label}
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-white/20">
                  {tab.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Products Section */}
        {(activeTab === "all" || activeTab === "products") && (
          <div className="mb-16">
            <motion.div
              variants={headerVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.1 }}
              className="text-center mb-8"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                Premium <span className="gradient-text">Products</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We provide high-quality products tailored to meet farmers' needs.
                From animal feed to other agricultural supplements, we ensure the
                best solutions for your farming enterprise.
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.1 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product, index) => (
                  <motion.div key={index} variants={childVariants}>
                    <Card image={product.image} title={product.title} description={product.description} />
                  </motion.div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 text-lg">No products found matching "{searchTerm}"</p>
                </div>
              )}
            </motion.div>
          </div>
        )}

        {/* Raw Materials Section */}
        {(activeTab === "all" || activeTab === "materials") && (
          <div>
            <motion.div
              variants={headerVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.1 }}
              className="text-center mb-8"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                Quality <span className="gradient-text">Raw Materials</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We source and supply premium raw materials to support farmers and
                agricultural enterprises in achieving sustainable growth.
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.1 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredMaterials.length > 0 ? (
                filteredMaterials.map((material, index) => (
                  <motion.div key={index} variants={childVariants}>
                    <Card image={material.image} title={material.title} description={material.description} />
                  </motion.div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 text-lg">No materials found matching "{searchTerm}"</p>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Services;
